/**
 * Route path mappings for different languages
 * Key is the Slovak path (default), values are translations per language
 * 
 * ⚠️ SINGLE SOURCE OF TRUTH for all routes
 * - Adding a new page? Add it here and it will automatically appear in:
 *   - Sitemap (generated at build time)
 *   - Hreflang tags
 *   - Localized navigation
 */

export type Language = 'sk' | 'en' | 'de' | 'uk' | 'ru' | 'es';

// Map Slovak path to translated paths for each language
export const routeTranslations: Record<string, Record<Language, string>> = {
  '/': {
    sk: '/',
    en: '/',
    de: '/',
    uk: '/',
    ru: '/',
    es: '/',
  },
  '/kalkulator': {
    sk: '/kalkulator',
    en: '/calculator',
    de: '/kalkulator',
    uk: '/kalkulyator',
    ru: '/kalkulyator',
    es: '/calculadora',
  },
  '/cennik': {
    sk: '/cennik',
    en: '/pricing',
    de: '/preisliste',
    uk: '/tsiny',
    ru: '/tseny',
    es: '/precios',
  },
  '/o-nas': {
    sk: '/o-nas',
    en: '/about-us',
    de: '/ueber-uns',
    uk: '/pro-nas',
    ru: '/o-nas',
    es: '/sobre-nosotros',
  },
  '/faq': {
    sk: '/faq',
    en: '/faq',
    de: '/faq',
    uk: '/faq',
    ru: '/faq',
    es: '/faq',
  },
  '/kontakt': {
    sk: '/kontakt',
    en: '/contact',
    de: '/kontakt',
    uk: '/kontakt',
    ru: '/kontakt',
    es: '/contacto',
  },
  '/ochrana-sukromia': {
    sk: '/ochrana-sukromia',
    en: '/privacy-policy',
    de: '/datenschutz',
    uk: '/polityka-konfidentsiynosti',
    ru: '/politika-konfidentsialnosti',
    es: '/politica-privacidad',
  },
  '/obchodne-podmienky': {
    sk: '/obchodne-podmienky',
    en: '/terms-and-conditions',
    de: '/agb',
    uk: '/umovy-obsluhovuvannya',
    ru: '/usloviya-obsluzhivaniya',
    es: '/terminos-condiciones',
  },
  '/sluzby': {
    sk: '/sluzby',
    en: '/services',
    de: '/dienstleistungen',
    uk: '/posluhy',
    ru: '/uslugi',
    es: '/servicios',
  },
  '/sluzby/uradne-preklady': {
    sk: '/sluzby/uradne-preklady',
    en: '/services/official-translations',
    de: '/dienstleistungen/beglaubigte-uebersetzungen',
    uk: '/posluhy/ofitsiyni-pereklady',
    ru: '/uslugi/ofitsialnye-perevody',
    es: '/servicios/traducciones-oficiales',
  },
  '/sluzby/dopyt': {
    sk: '/sluzby/dopyt',
    en: '/services/inquiry',
    de: '/dienstleistungen/anfrage',
    uk: '/posluhy/zapyt',
    ru: '/uslugi/zapros',
    es: '/servicios/consulta',
  },
  '/sluzby/apostila': {
    sk: '/sluzby/apostila',
    en: '/services/apostille',
    de: '/dienstleistungen/apostille',
    uk: '/posluhy/apostyl',
    ru: '/uslugi/apostil',
    es: '/servicios/apostilla',
  },
  '/sluzby/superlegalizacia': {
    sk: '/sluzby/superlegalizacia',
    en: '/services/superlegalization',
    de: '/dienstleistungen/ueberbeglaubigung',
    uk: '/posluhy/superlegalizatsiya',
    ru: '/uslugi/superlegalizatsiya',
    es: '/servicios/superlegalizacion',
  },
  '/sluzby/zahranicne-dokumenty': {
    sk: '/sluzby/zahranicne-dokumenty',
    en: '/services/foreign-documents',
    de: '/dienstleistungen/auslaendische-dokumente',
    uk: '/posluhy/inozemni-dokumenty',
    ru: '/uslugi/inostrannye-dokumenty',
    es: '/servicios/documentos-extranjeros',
  },
  // Blog routes
  '/blog': {
    sk: '/blog',
    en: '/blog',
    de: '/blog',
    uk: '/blog',
    ru: '/blog',
    es: '/blog',
  },
};

// Old paths that need redirects (for backward compatibility)
export const legacyRedirects: Array<{ from: string; to: string }> = [
  // Ukrainian old -> new
  { from: '/uk/kalkulator', to: '/uk/kalkulyator' },
  { from: '/uk/cennik', to: '/uk/tsiny' },
  { from: '/uk/o-nas', to: '/uk/pro-nas' },
  { from: '/uk/ochrana-sukromia', to: '/uk/polityka-konfidentsiynosti' },
  { from: '/uk/obchodne-podmienky', to: '/uk/umovy-obsluhovuvannya' },
  { from: '/uk/sluzby/uradne-preklady', to: '/uk/posluhy/ofitsiyni-pereklady' },
  { from: '/uk/sluzby/legalizacia', to: '/uk/posluhy/lehalizatsiya' },
  // Russian old -> new
  { from: '/ru/kalkulator', to: '/ru/kalkulyator' },
  { from: '/ru/cennik', to: '/ru/tseny' },
  { from: '/ru/ochrana-sukromia', to: '/ru/politika-konfidentsialnosti' },
  { from: '/ru/obchodne-podmienky', to: '/ru/usloviya-obsluzhivaniya' },
  { from: '/ru/sluzby/uradne-preklady', to: '/ru/uslugi/ofitsialnye-perevody' },
  { from: '/ru/sluzby/legalizacia', to: '/ru/uslugi/legalizatsiya' },
];

// Get the Slovak (canonical) path from any localized path
export function getCanonicalPath(localizedPath: string, language: Language): string {
  // Remove language prefix if present
  const pathWithoutLang = localizedPath.replace(/^\/(en|de|uk|ru|es)/, '') || '/';

  // If Slovak, the path is already canonical
  if (language === 'sk') {
    return pathWithoutLang;
  }

  // 1) Exact match
  for (const [skPath, translations] of Object.entries(routeTranslations)) {
    if (translations[language] === pathWithoutLang) {
      return skPath;
    }
  }

  // 2) Prefix match (supports dynamic routes like /services/foreign-documents/:slug)
  let bestMatch:
    | { skPath: string; localizedBase: string }
    | undefined;

  for (const [skPath, translations] of Object.entries(routeTranslations)) {
    const localizedBase = translations[language];
    if (
      pathWithoutLang === localizedBase ||
      pathWithoutLang.startsWith(`${localizedBase}/`)
    ) {
      if (!bestMatch || localizedBase.length > bestMatch.localizedBase.length) {
        bestMatch = { skPath, localizedBase };
      }
    }
  }

  if (bestMatch) {
    const suffix = pathWithoutLang.slice(bestMatch.localizedBase.length);
    return `${bestMatch.skPath}${suffix}`;
  }

  // If not found in translations, return the path as-is
  return pathWithoutLang;
}

// Get the localized path for a given Slovak path and language
export function getLocalizedPath(skPath: string, language: Language): string {
  // Handle external links
  if (
    skPath.startsWith('http') ||
    skPath.startsWith('#') ||
    skPath.startsWith('mailto:') ||
    skPath.startsWith('tel:')
  ) {
    return skPath;
  }

  // Normalize path
  const normalizedPath = skPath.startsWith('/') ? skPath : `/${skPath}`;

  // 1) Exact match
  const exact = routeTranslations[normalizedPath];
  if (exact) {
    const translatedPath = exact[language] || normalizedPath;
    return language === 'sk'
      ? translatedPath
      : translatedPath === '/'
        ? `/${language}`
        : `/${language}${translatedPath}`;
  }

  // 2) Prefix match (supports dynamic routes like /sluzby/zahranicne-dokumenty/:slug)
  let bestPrefix: string | undefined;
  for (const key of Object.keys(routeTranslations)) {
    if (normalizedPath === key || normalizedPath.startsWith(`${key}/`)) {
      if (!bestPrefix || key.length > bestPrefix.length) bestPrefix = key;
    }
  }

  if (bestPrefix) {
    const translatedPrefix = routeTranslations[bestPrefix]?.[language] || bestPrefix;
    const suffix = normalizedPath.slice(bestPrefix.length);
    const translatedPath = `${translatedPrefix}${suffix}`;

    return language === 'sk'
      ? translatedPath
      : translatedPath === '/'
        ? `/${language}`
        : `/${language}${translatedPath}`;
  }

  // Fallback: no translation known
  return language === 'sk' ? normalizedPath : `/${language}${normalizedPath}`;
}

// Generate all route paths for a given language (for React Router)
export function getAllRoutePaths(language: Language): { skPath: string; localPath: string }[] {
  return Object.entries(routeTranslations).map(([skPath, translations]) => ({
    skPath,
    localPath: language === 'sk' ? translations[language] : `/${language}${translations[language]}`,
  }));
}
